// React components
import { useEffect, useState } from "react";

// @mui material components
import AppBar from "@mui/material/AppBar";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Grid from "@mui/material/Grid";

// Material Dashboard 2 PRO React TS components
import MDBox from "components/MDBox";

// Material Dashboard 2 PRO React TS examples components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import GradientLineChart from "examples/Charts/LineCharts/GradientLineChart";
import DefaultLineChart from "examples/Charts/LineCharts/DefaultLineChart";

// Custom components
import CountryTable from "../Tables/CountryTable";

// API queries
import { useEpidemiologyByLocCodeAndOptions } from "api/features/epidemiology/queries";

// Contexts
import { useFormAnalyticsContext } from "contexts/contextFormAnalytics";

// Data formatting functions
import {
  formatCountryData,
  formatEpidemiologyDataToTable,
} from "../../data/formatting/dataToTable";
import { formatGroupedDataToChart } from "../../data/formatting/dataToChart";

// Assets
import breakpoints from "assets/theme-dark/base/breakpoints";

//Config
import { CHARTS_ANALYTICS_X_MIN_MAX } from "config/config";

function MainContent(): JSX.Element {
  const { year, countryData, epidemiologyData, isLoading, isError, errorMessage, refetch } =
    useFormAnalyticsContext();
  const {
    incidenceData,
    survivalData,
    prevalenceData,
    deathData,
    lifeYearsData,
    worldStandardPopData,
  } = epidemiologyData ?? {};

  const [tabValue, setTabValue] = useState<number>(0);
  const handleSetTabValue = (event: any, newValue: number) => setTabValue(newValue);
  const isAssumptionsTab = tabValue === 1 || tabValue === 0;
  const isCoreResultsTab = tabValue === 2 || tabValue === 0;
  const isDerivedMeasuresTab = tabValue === 3 || tabValue === 0;
  const isDerivedFunctionsTab = tabValue === 4 || tabValue === 0;
  const isLifeYearsTab = tabValue === 5 || tabValue === 0;
  const cardStatus = { isLoading, isError, errorMessage, onRetry: refetch };

  return (
    <DashboardLayout hasSideform>
      <DashboardNavbar hasSideform />
      <MDBox py={3}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <CountryTable
              icon={{ component: "public" }}
              title="Country Details"
              description={`Selected country: ${countryData?.country_LocName || "N/A"}`}
              rows={formatCountryData(countryData)}
              {...cardStatus}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <CountryTable
              icon={{ component: "summarize" }}
              title="Summary of Results"
              description={`Selected Year: ${year || "N/A"}`}
              withTitleCell
              rows={formatEpidemiologyDataToTable(epidemiologyData, year)}
              {...cardStatus}
            />
          </Grid>
          {!isLoading && !isError && (
            <Grid item xs={12}>
              <AppBar position="static">
                <Tabs
                  orientation={
                    window.innerWidth < breakpoints.values.md ? "vertical" : "horizontal"
                  }
                  value={tabValue}
                  onChange={handleSetTabValue}
                >
                  <Tab label="All" />
                  <Tab label="Assumptions" />
                  <Tab label="Core Results" />
                  <Tab label="Derived Measures" />
                  <Tab label="Derived Functions" />
                  <Tab label="Life-Years Gained and Lost" />
                </Tabs>
              </AppBar>
            </Grid>
          )}
          {isAssumptionsTab && (
            <>
              <Grid item xs={12} md={6}>
                <DefaultLineChart
                  {...cardStatus}
                  icon={{ component: "stacked_line_chart" }}
                  title="Population size - Stacked graph"
                  description="(in 1000s), by age"
                  stacked
                  xAxis="Calendar year (start of year)"
                  xMinMax={CHARTS_ANALYTICS_X_MIN_MAX}
                  chart={formatGroupedDataToChart(incidenceData?.demoSize1yrData, {
                    labels: ["0-19 years", "20-39 years", "40-59 years", "60+ years"],
                    stacked: true,
                  })}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <DefaultLineChart
                  {...cardStatus}
                  icon={{ component: "timeline" }}
                  title="Incidence rates of T1D"
                  description="per 100 000, by age group"
                  pointed
                  xAxis="Calendar year"
                  xMinMax={CHARTS_ANALYTICS_X_MIN_MAX}
                  tooltipPrecision={2}
                  chart={formatGroupedDataToChart(incidenceData?.incidenceRatesData, {
                    labels: ["0-19 years", "20-39 years", "40-59 years", "60+ years", "All"],
                  })}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <DefaultLineChart
                  {...cardStatus}
                  icon={{ component: "show_chart" }}
                  title="1-year survival in T1D"
                  description="by age group, relative to general population"
                  xAxis="Calendar year at start"
                  xMinMax={CHARTS_ANALYTICS_X_MIN_MAX}
                  tooltipPrecision={2}
                  chart={formatGroupedDataToChart(survivalData?.relativeSurvivalByAgeGroupData, {
                    labels: ["0-19 years", "20-39 years", "40-59 years", "60+ years", "All"],
                  })}
                />
              </Grid>
            </>
          )}
          {isCoreResultsTab && (
            <>
              <Grid item xs={12} md={6}>
                <DefaultLineChart
                  {...cardStatus}
                  icon={{ component: "stacked_line_chart" }}
                  title="Prevalent numbers of T1D - Stacked graph"
                  description="by current age"
                  stacked
                  xAxis="Calendar year (end of year)"
                  xMinMax={CHARTS_ANALYTICS_X_MIN_MAX}
                  chart={formatGroupedDataToChart(prevalenceData?.prevCoreData, {
                    labels: ["0-19 years", "20-39 years", "40-59 years", "60+ years"],
                    stacked: true,
                  })}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <DefaultLineChart
                  {...cardStatus}
                  icon={{ component: "stacked_line_chart" }}
                  title="Incident numbers of T1D - Stacked graph"
                  description="by current age"
                  stacked
                  xAxis="Calendar year"
                  xMinMax={CHARTS_ANALYTICS_X_MIN_MAX}
                  chart={formatGroupedDataToChart(incidenceData?.incCasesData, {
                    labels: ["0-19 years", "20-39 years", "40-59 years", "60+ years"],
                    stacked: true,
                  })}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <DefaultLineChart
                  {...cardStatus}
                  icon={{ component: "stacked_line_chart" }}
                  title="Annual numbers of all-cause deaths in T1D - Stacked graph"
                  description="(core scenario), by current age"
                  stacked
                  xAxis="Calendar year"
                  xMinMax={CHARTS_ANALYTICS_X_MIN_MAX}
                  chart={formatGroupedDataToChart(deathData?.deathsCoreData, {
                    labels: ["0-19 years", "20-39 years", "40-59 years", "60+ years"],
                    stacked: true,
                  })}
                />
              </Grid>
            </>
          )}
          {isDerivedMeasuresTab && (
            <>
              <Grid item xs={12} md={6}>
                <DefaultLineChart
                  {...cardStatus}
                  icon={{ component: "show_chart" }}
                  title="Prevalent numbers of T1D (core scenario)"
                  description="by current age"
                  xAxis="Calendar year (end of year)"
                  xMinMax={CHARTS_ANALYTICS_X_MIN_MAX}
                  chart={formatGroupedDataToChart(
                    {
                      0: prevalenceData?.prevCoreData?.[0],
                      1: prevalenceData?.prevCoreData?.["total"],
                    },
                    {
                      labels: ["0-19 years", "All pages"],
                    }
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <DefaultLineChart
                  {...cardStatus}
                  icon={{ component: "show_chart" }}
                  title="Prevalent numbers of T1D (all age groups)"
                  description="by alternative scenarios"
                  xAxis="Calendar year (end of year)"
                  xMinMax={CHARTS_ANALYTICS_X_MIN_MAX}
                  chart={formatGroupedDataToChart(
                    {
                      0: prevalenceData?.prevOptimalData?.["total"],
                      1: prevalenceData?.prevCoreData?.["total"],
                      2: prevalenceData?.prevWorstData?.["total"],
                    },
                    {
                      labels: ["Optimal scenario", "Core scenario", "Worst scenario"],
                    }
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <DefaultLineChart
                  {...cardStatus}
                  icon={{ component: "show_chart" }}
                  title="Standardized measures of T1D (core scenario)"
                  description="WHO standard population 2000-2025"
                  xAxis="Calendar year"
                  xMinMax={CHARTS_ANALYTICS_X_MIN_MAX}
                  yAxis={{
                    yInc: "Incidence rate (per 100,000)",
                    yPrev: "Prevalence proportion (per 1,000)",
                  }}
                  tooltipPrecision={2}
                  chart={formatGroupedDataToChart(
                    {
                      0: worldStandardPopData?.incidenceRatesData,
                      1: worldStandardPopData?.prevalenceData,
                    },
                    {
                      labels: ["Stand. incidence rate", "Stand. prevalence proportion"],
                      yAxisID: ["yInc", "yPrev"],
                    }
                  )}
                />
              </Grid>
            </>
          )}
          {isDerivedFunctionsTab && (
            <>
              <Grid item xs={12} md={6}>
                <DefaultLineChart
                  {...cardStatus}
                  icon={{ component: "show_chart" }}
                  title="Mortality rates (per 100 patient-years)"
                  description="by current age groups"
                  xAxis="Calendar year"
                  xMinMax={CHARTS_ANALYTICS_X_MIN_MAX}
                  chart={formatGroupedDataToChart(deathData?.mortalityRatesData, {
                    labels: ["0-19 years", "20-39 years", "40-59 years", "60+ years", "All"],
                  })}
                  legendPosition="top"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <DefaultLineChart
                  {...cardStatus}
                  icon={{ component: "show_chart" }}
                  title="Estimated 10-years survival (%) from start"
                  description="by year and selected age levels"
                  xAxis="Calendar year at start"
                  xMinMax={CHARTS_ANALYTICS_X_MIN_MAX}
                  chart={formatGroupedDataToChart(
                    survivalData?.relativeSurvival10yrByAgeGroupData,
                    {
                      labels: [
                        "Age 9 as start",
                        "Age 28 as start",
                        "Age 48 as start",
                        "Age 70 as start",
                      ],
                    }
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <DefaultLineChart
                  {...cardStatus}
                  icon={{ component: "show_chart" }}
                  title="Estimated 25-years survival (%) from start"
                  description="by year and selected age levels"
                  xAxis="Calendar year at start"
                  xMinMax={CHARTS_ANALYTICS_X_MIN_MAX}
                  chart={formatGroupedDataToChart(
                    survivalData?.relativeSurvival25yrByAgeGroupData,
                    {
                      labels: [
                        "Age 9 as start",
                        "Age 28 as start",
                        "Age 48 as start",
                        "Age 70 as start",
                      ],
                    }
                  )}
                />
              </Grid>
            </>
          )}
          {isLifeYearsTab && (
            <>
              <Grid item xs={12} md={6}>
                <DefaultLineChart
                  {...cardStatus}
                  icon={{ component: "show_chart" }}
                  title="Annual number of gained and lost life-years in T1D (core scenario)"
                  description="by current age groups"
                  xAxis="Calendar year"
                  xMinMax={CHARTS_ANALYTICS_X_MIN_MAX}
                  chart={formatGroupedDataToChart(
                    {
                      0: lifeYearsData?.lostLifeYearsData?.[0],
                      1: lifeYearsData?.lostLifeYearsData?.["total"],
                      2: lifeYearsData?.gainedLifeYearsData?.[0],
                      3: lifeYearsData?.gainedLifeYearsData?.["total"],
                    },
                    {
                      labels: [
                        "Lost life-years (0-19 years)",
                        "Lost life-years (All ages groups)",
                        "Gained life-years (0-19 years)",
                        "Gained life-years (All ages groups)",
                      ],
                    }
                  )}
                />
              </Grid>
            </>
          )}
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default MainContent;
